import { NewAdminPages, newAuthPages, newAuthSubAdminAccess, writeAccess } from '../../../services/page-service';
import { AVAILABLE_LANGUAGES, DEFAULT_BOX_NAMES } from './constants';
import { OfferAudioVariantConfig, OfferConfig, OfferLanguageEntry } from './types';

export function cloneData<T>(data: T): T {
    return JSON.parse(JSON.stringify(data)) as T;
}

export function generateId(): string {
    return crypto.randomUUID();
}

function normalizeTimeValue(timeValue: string): string {
    const value = (timeValue || '').trim();
    if (!value) return '';
    if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    if (/^\d{2}:\d{2}:\d{2}/.test(value)) return value.slice(0, 8);
    return value;
}

function splitDateTimeValue(dateTimeValue: string): { date: string; time: string } {
    const raw = (dateTimeValue || '').trim();
    if (!raw) return { date: '', time: '' };

    if (raw.includes('T')) {
        const [datePart, timePartRaw = ''] = raw.split('T');
        const timePart = timePartRaw.replace('Z', '').split('.')[0];
        return { date: datePart, time: normalizeTimeValue(timePart) };
    }

    if (raw.includes(' ')) {
        const [datePart, timePartRaw = ''] = raw.split(' ');
        return { date: datePart, time: normalizeTimeValue(timePartRaw) };
    }

    return { date: raw, time: '' };
}

export function getDateTimeValue(dateValue: string, timeValue = ''): string {
    const datePart = (dateValue || '').trim();
    const timePart = normalizeTimeValue(timeValue);

    if (!datePart) return '';

    if (datePart.includes('T') || datePart.includes(' ')) {
        return datePart;
    }

    return timePart ? `${datePart} ${timePart}` : datePart;
}

export function formatDate(dateValue: string, timeValue = '') {
    const dateTimeValue = getDateTimeValue(dateValue, timeValue);
    const date = new Date(dateTimeValue);
    if (Number.isNaN(date.getTime())) {
        return dateTimeValue || dateValue;
    }
    return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
}

export function deriveIsActive(
    validFromDate: string,
    validTillDate: string,
    validFromTime = '',
    validTillTime = ''
): boolean {
    const validFrom = getDateTimeValue(validFromDate, validFromTime);
    const validTill = getDateTimeValue(validTillDate, validTillTime);

    if (!validFrom || !validTill) return false;
    const from = new Date(validFrom);
    const till = new Date(validTill);
    if (Number.isNaN(from.getTime()) || Number.isNaN(till.getTime())) {
        return false;
    }
    const now = new Date();
    return now >= from && now <= till;
}

export function getVariantStateKey(offerName: string, languageName: string, variantId: string) {
    return `${offerName}__${languageName}__${variantId}`;
}

export function createEmptyVariant(boxName = ''): OfferAudioVariantConfig {
    return {
        id: generateId(),
        box_name: boxName,
        package_id: '',
        audio_file: '',
        audioUrl: '',
    };
}

function createDefaultLanguage(languageName: string): OfferLanguageEntry {
    return {
        language_name: languageName,
        variants: DEFAULT_BOX_NAMES.map((name) => createEmptyVariant(name)),
    };
}

function normalizeVariant(variant: OfferAudioVariantConfig): OfferAudioVariantConfig {
    return {
        ...variant,
        id: variant.id || generateId(),
        box_name: variant.box_name || '',
        package_id: variant.package_id || '',
        audio_file: variant.audio_file || '',
        audioUrl: variant.audioUrl || '',
    };
}

export function withMandatoryLanguages(offer: OfferConfig): OfferConfig {
    const existingByLanguage = new Map(offer.languages.map((language) => [language.language_name, language]));
    const mandatoryLanguages = AVAILABLE_LANGUAGES.map((languageName) => {
        const current = existingByLanguage.get(languageName);
        if (!current) {
            return createDefaultLanguage(languageName);
        }

        return {
            ...current,
            variants: (current.variants || []).map(normalizeVariant),
        };
    });

    const extraLanguages = offer.languages
        .filter((language) => !AVAILABLE_LANGUAGES.includes(language.language_name))
        .map((language) => ({
            ...language,
            variants: (language.variants || []).map(normalizeVariant),
        }));

    return {
        ...offer,
        languages: [...mandatoryLanguages, ...extraLanguages],
    };
}

export function createEmptyOffer(flowId: string): OfferConfig {
    return {
        offer_name: '',
        flow_id: flowId,
        valid_from: '',
        valid_from_time: '',
        valid_till: '',
        valid_till_time: '',
        brand: 'DISH',
        languages: AVAILABLE_LANGUAGES.map((languageName) => createDefaultLanguage(languageName)),
    };
}

function getNormalizedAudioUrl(variant: OfferAudioVariantConfig): string {
    return (variant.audioUrl || variant.audio_file || '').trim();
}

export function hasVariantRequiredFieldErrors(variant: OfferAudioVariantConfig): boolean {
    return (
        variant.box_name.trim().length === 0 ||
        variant.package_id.trim().length === 0 ||
        getNormalizedAudioUrl(variant).length === 0
    );
}

export function hasLanguageRequiredFieldErrors(language: OfferLanguageEntry): boolean {
    if (!language.variants || language.variants.length === 0) {
        return true;
    }
    return language.variants.some((variant) => hasVariantRequiredFieldErrors(variant));
}

export function hasMandatoryLanguageErrors(offer: OfferConfig): boolean {
    const languageSet = new Set(offer.languages.map((language) => language.language_name));
    return AVAILABLE_LANGUAGES.some((languageName) => !languageSet.has(languageName));
}

export function hasOfferRequiredFieldErrors(offer: OfferConfig): boolean {
    if (hasMandatoryLanguageErrors(offer)) {
        return true;
    }

    return offer.languages.some((language) => hasLanguageRequiredFieldErrors(language));
}

export function isOfferDirty(offer: OfferConfig, snapshotOffer: OfferConfig | null): boolean {
    if (!snapshotOffer) return true;
    return JSON.stringify(offer) !== JSON.stringify(snapshotOffer);
}

export function isLanguageDirty(
    offer: OfferConfig,
    snapshotOffer: OfferConfig | null,
    languageIndex: number
): boolean {
    if (!snapshotOffer) return true;
    const currentLang = offer.languages[languageIndex];
    const snapshotLang = snapshotOffer.languages.find(
        (l) => l.language_name === currentLang?.language_name
    );
    if (!snapshotLang) return true;
    return JSON.stringify(currentLang) !== JSON.stringify(snapshotLang);
}

export function getLanguageVariantCount(lang: OfferLanguageEntry): number {
    return lang.variants.length;
}

function getNormalizedDateAndTime(dateValue: string, timeValue: string): { date: string; time: string } {
    const trimmedDate = (dateValue || '').trim();
    const trimmedTime = normalizeTimeValue(timeValue);

    if (trimmedDate && !trimmedTime) {
        const split = splitDateTimeValue(trimmedDate);
        return {
            date: split.date,
            time: split.time,
        };
    }

    return {
        date: splitDateTimeValue(trimmedDate).date || trimmedDate,
        time: trimmedTime,
    };
}

export function buildSavePayload(offer: OfferConfig): OfferConfig {
    const validFrom = getNormalizedDateAndTime(offer.valid_from, offer.valid_from_time);
    const validTill = getNormalizedDateAndTime(offer.valid_till, offer.valid_till_time);

    return {
        ...offer,
        valid_from: validFrom.date,
        valid_from_time: validFrom.time,
        valid_till: validTill.date,
        valid_till_time: validTill.time,
        languages: offer.languages.map((lang) => ({
            ...lang,
            variants: lang.variants.map((v) => ({
                ...v,
                box_name: v.box_name.trim(),
                package_id: v.package_id.trim(),
                audio_file: (v.audio_file || '').trim(),
                audioUrl: (v.audioUrl || v.audio_file || '').trim(),
            })),
        })),
    };
}

export function getOfferPermissions() {
    const hasBaseAccess =
        newAuthPages(NewAdminPages.OFFER_LANGUAGE_CONFIGURATION) ||
        newAuthSubAdminAccess(NewAdminPages.OFFER_LANGUAGE_CONFIGURATION, 'View') ||
        newAuthSubAdminAccess(NewAdminPages.OFFER_LANGUAGE_CONFIGURATION, 'Edit') ||
        newAuthSubAdminAccess(NewAdminPages.OFFER_LANGUAGE_CONFIGURATION, 'Full');

    const canEdit =
        writeAccess() &&
        (newAuthPages(NewAdminPages.OFFER_LANGUAGE_CONFIGURATION) ||
            newAuthSubAdminAccess(NewAdminPages.OFFER_LANGUAGE_CONFIGURATION, 'Edit') ||
            newAuthSubAdminAccess(NewAdminPages.OFFER_LANGUAGE_CONFIGURATION, 'Full'));

    return { hasBaseAccess, canEdit };
}

export function downloadFileByURL(url: string, fileName: string) {
    try {
        // Create an anchor element
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = fileName;

        // Set the target attribute to '_blank' to open in a new tab
        downloadLink.target = '_blank';

        // Append the anchor to the document and trigger the click event
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // Clean up and remove the anchor element
        document.body.removeChild(downloadLink);
    } catch (e) {
        console.error(e);
    }
} 
